import { ChangeEvent, useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import { Box, CloseButton, Spacer } from '@chakra-ui/react'

import {
  BadgeCounter,
  CloseIcon,
  Box as DarkBox,
  DarkProvider,
  IconButton,
  SearchIcon,
  TextInput,
} from '@hivewatch/dark'

import { CountBadge } from '@/components/ui'
import { SearchInput } from '@/components/ui'
import {
  closeSOPIncidentDrawer,
  selectSOPIncidentDrawerState,
  setSOPIncidentNameLike,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, SEARCH, trackEvent } from '@/utils/analytics'

interface SOPIncidentHeaderIProps {
  facilityId: string
  incidentId?: string
  sopsCount: number
}

export const SOPIncidentHeader = ({
  facilityId,
  incidentId,
  sopsCount,
}: SOPIncidentHeaderIProps) => {
  const dispatch = useDispatch()
  const colorScheme = useLightMode()
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)

  const { sopNameLike } = useSelector(selectSOPIncidentDrawerState)
  const [nameFilter, setNameFilter] = useState(sopNameLike)
  const [debouncedNameFilter] = useDebounce(nameFilter, 1000)

  useEffect(() => {
    dispatch(setSOPIncidentNameLike(debouncedNameFilter))
    if (debouncedNameFilter) {
      trackEvent(`${SEARCH} SOP Incident Drawer`, {
        search_term: debouncedNameFilter,
        facility_id: facilityId,
        incident_id: incidentId,
      })
    }
  }, [debouncedNameFilter])

  const onNameFilterChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setNameFilter(e.target.value)
  }

  const onClose = () => {
    trackEvent(`${BUTTON_PRESS} SOP Incident Drawer Close`, {
      facility_id: facilityId,
      incident_id: incidentId,
      closed_via: 'close_button',
    })
    dispatch(closeSOPIncidentDrawer())
  }

  if (isRebrand) {
    const prefix = colorScheme === 'light' ? 'light.' : ''
    return (
      <DarkProvider>
        <DarkBox
          alignItems='center'
          display='flex'
          gap='12px'
          height='64px'
          pb='16px'
          pt='16px'
          px='48px'
        >
          <DarkBox
            as='span'
            color={`${prefix}text.primary`}
            fontFamily="'Soehne Kraftig', 'Söhne Kräftig', sans-serif"
            fontSize='20px'
            fontWeight='500'
            lineHeight='28px'
            whiteSpace='nowrap'
          >
            SOPs
          </DarkBox>
          <BadgeCounter
            colorScheme={colorScheme}
            count={sopsCount ?? 0}
            variant='button'
          />
          <DarkBox flex='1' minWidth={0} />
          <TextInput
            colorScheme={colorScheme}
            leftIcon={<SearchIcon colorScheme={colorScheme} size={14} />}
            onChange={onNameFilterChange}
            placeholder='Search'
            value={nameFilter ?? ''}
            width='220px'
          />
          <IconButton
            _active={{ bg: 'transparent' }}
            aria-label='close SOP drawer'
            colorScheme={colorScheme}
            dimension={36}
            hoverBgColor='transparent'
            icon={<CloseIcon size={11} />}
            onClick={onClose}
          />
        </DarkBox>
      </DarkProvider>
    )
  }

  return (
    <Box
      alignItems='center'
      borderBottom='1px solid #D5DCE4'
      display='flex'
      px='24px'
      py='12px'
    >
      <CountBadge count={sopsCount} title='SOPs' />
      <Spacer />
      <Box>
        <SearchInput
          onChange={onNameFilterChange}
          placeholder='Search or Filter'
          value={nameFilter ?? ''}
        />
      </Box>
      <CloseButton ml={4} onClick={onClose} />
    </Box>
  )
}
